import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useFormularioDinamico } from '../../../../hooks/useFormularioDinamico';

function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

export default function FormatoPracticasHigienicas({ usuario, onBack }) {
  const {
    formulario, respuestas: datosServer, loading, enviando, sedes,
    handleChange: handleServerChange, handleSubmit
  } = useFormularioDinamico('practicas-higienicas', usuario);

  const [respuestasEvaluacion, setRespuestasEvaluacion] = useState({});
  const [customAlert, setCustomAlert] = useState({ show: false, message: "", type: "success" });
  const [empleadosDB, setEmpleadosDB] = useState([]);
  const [cargandoEmpleados, setCargandoEmpleados] = useState(false);
  const [buscarTodasSedes, setBuscarTodasSedes] = useState(false);

  useEffect(() => {
    const cargarEmpleados = async () => {
      setCargandoEmpleados(true);
      try {
        const token = localStorage.getItem("token");
        const API_URL = process.env.REACT_APP_API_URL;
        const resEmp = await fetch(`${API_URL}/api/empleados`, {
          headers: { ...(token && { Authorization: `Bearer ${token}` }) }
        });
        const dataEmp = await resEmp.json();
        setEmpleadosDB(Array.isArray(dataEmp) ? dataEmp : (dataEmp.data || []));
      } catch (error) {
        console.error("Error cargando empleados:", error);
      } finally {
        setCargandoEmpleados(false);
      }
    };
    cargarEmpleados();
  }, []);

  useEffect(() => {
    if (!datosServer.fechaEvento) handleServerChange('fechaEvento', new Date().toISOString().split('T')[0]);
    if (!datosServer.turno) handleServerChange('turno', '8:00 AM');
    if (!datosServer.genero) handleServerChange('genero', 'MUJER');
  }, [datosServer, handleServerChange]);

  // Extraer las listas parametrizadas desde la BD (configuración del campo)
  const campoConfig = formulario?.campos?.find(c => c.nombre_campo === 'nombre')?.opciones || {};
  const aspectosMujeres = campoConfig.mujeres || [];
  const aspectosHombres = campoConfig.hombres || [];
  const aspectosSalud = campoConfig.salud || [];

  const aspectosActuales = datosServer.genero === 'MUJER' ? aspectosMujeres : aspectosHombres;

  const listaParaMostrar = useMemo(() => {
    return empleadosDB.filter(emp => {
      const estaActivo = !emp.empl_fecret || String(emp.empl_fecret).toUpperCase() === 'NULL' || emp.empl_fecret === '0000-00-00';
      if (!estaActivo) return false;
      if (buscarTodasSedes) return true; 
      if (!emp.area_nombre || !datosServer.pdv) return false;
      return emp.area_nombre.toUpperCase() === datosServer.pdv.toUpperCase();
    });
  }, [empleadosDB, datosServer.pdv, buscarTodasSedes]);

  const handleCustomChange = (e) => {
    const { name, value } = e.target;
    if (name === 'pdv') {
      handleServerChange('pdv', value);
      handleServerChange('documento', '');
      handleServerChange('nombre', '');
      handleServerChange('cargo', '');
      setRespuestasEvaluacion({});
    } else if (name === 'nombre') {
      const empSeleccionado = listaParaMostrar.find(emp => emp.terc_nom === value);
      if (empSeleccionado) {
        handleServerChange('nombre', empSeleccionado.terc_nom);
        handleServerChange('documento', empSeleccionado.terc_nit);
        handleServerChange('cargo', empSeleccionado.empl_cargo);
        setRespuestasEvaluacion({});
      } else {
        handleServerChange('nombre', value);
      }
    } else {
      handleServerChange(name, value);
      if (name === 'genero') setRespuestasEvaluacion({});
    }
  };

  const setRespuestaItem = (id, valor) => {
    setRespuestasEvaluacion(prev => ({ ...prev, [id]: valor }));
  };

  const marcarTodoCumple = () => {
    const nuevasRespuestas = {};
    aspectosActuales.forEach(item => { nuevasRespuestas[item.id] = 'SI'; });
    aspectosSalud.forEach(item => { nuevasRespuestas[item.id] = 'NO'; });
    setRespuestasEvaluacion(nuevasRespuestas);
  };

  const guardarLocal = (e) => {
    if (!datosServer.pdv || !datosServer.documento || !datosServer.nombre || !datosServer.turno || !datosServer.cargo) {
      return setCustomAlert({ show: true, message: "Sede, Turno, Documento, Nombre y Cargo son obligatorios.", type: "warning" });
    }
    
    if (Object.keys(respuestasEvaluacion).length < (aspectosActuales.length + aspectosSalud.length)) {
      return setCustomAlert({ show: true, message: "Debes evaluar todos los aspectos higiénicos y de salud.", type: "warning" });
    }

    handleServerChange('evaluacion_detallada', respuestasEvaluacion);

    handleSubmit(e, () => {
      setCustomAlert({ show: true, message: "Evaluación guardada exitosamente.", type: "success" });
      setTimeout(() => {
        handleServerChange('documento', '');
        handleServerChange('nombre', '');
        handleServerChange('cargo', '');
        handleServerChange('accionCorrectiva', '');
        setRespuestasEvaluacion({});
        setCustomAlert(prev => ({ ...prev, show: false }));
      }, 1200);
    });
  };

  if (loading || !formulario) return <div className="p-10 text-center text-white font-bold">Cargando Prácticas Higiénicas...</div>;

  const inputClass = "w-full h-10 px-3 bg-white/80 border-transparent focus:border-fuchsia-400 rounded-xl text-xs font-bold text-slate-800 shadow-sm transition-all outline-none cursor-pointer";

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-4xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      <div className="bg-gradient-to-r from-fuchsia-600/90 to-purple-800/90 border-b border-white/20 px-6 py-4 z-40 flex items-center shadow-md shrink-0">
        <button onClick={onBack} className="mr-4 w-10 h-10 flex-none bg-white/20 hover:bg-white/40 text-white flex items-center justify-center rounded-full transition-all border border-white/40 shadow-sm cursor-pointer">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
        <div>
          <h1 className="text-lg sm:text-xl font-black text-white leading-tight">{formulario.titulo}</h1>
          <span className="bg-white/20 text-white border border-white/30 text-[10px] font-bold px-2 py-0.5 rounded-md mt-1 inline-block">CÓDIGO: FT-CD-01 | Versión: {formulario.version}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 custom-scrollbar">
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Fecha *</label>
              <input type="date" name="fechaEvento" value={datosServer.fechaEvento || ""} onChange={handleCustomChange} className={inputClass} />
            </div>
            
            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Sede *</label>
              <div className="relative w-full h-10">
                <select name="pdv" value={datosServer.pdv || ""} onChange={handleCustomChange} className={`${inputClass} appearance-none pr-8 absolute inset-0`}>
                  <option value="" disabled>-- Seleccione sede --</option>
                  {sedes.map(sede => (
                    <option key={sede.id} value={sede.nombre.toUpperCase()}>{sede.nombre}</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 text-[10px] font-black">▼</div>
              </div>
            </div>

            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Turno *</label>
              <div className="relative w-full h-10">
                <select name="turno" value={datosServer.turno || ""} onChange={handleCustomChange} className={`${inputClass} appearance-none pr-8 absolute inset-0`}>
                  <option value="8:00 AM">8:00 AM (Mañana)</option><option value="2:00 PM">2:00 PM (Tarde)</option><option value="10:00 PM">10:00 PM (Noche)</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 text-[10px] font-black">▼</div>
              </div>
            </div>
            
            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Género *</label>
              <div className="relative w-full h-10">
                <select name="genero" value={datosServer.genero || ""} onChange={handleCustomChange} className={`${inputClass} appearance-none pr-8 absolute inset-0`}>
                  <option value="MUJER">MUJER</option><option value="HOMBRE">HOMBRE</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 text-[10px] font-black">▼</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-slate-200/60 pt-4">
            <div className="relative flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Nombre del Empleado *</label>
              <input 
                type="text" name="nombre" list="listaEmpleados" value={datosServer.nombre || ""} onChange={handleCustomChange} 
                placeholder={cargandoEmpleados ? "Cargando BD..." : datosServer.pdv ? "Escribe o elige nombre..." : "Elige sede primero"} 
                disabled={!datosServer.pdv || cargandoEmpleados}
                className={`${inputClass} disabled:opacity-50 disabled:cursor-not-allowed`} autoComplete="off"
              />
              <datalist id="listaEmpleados">
                {listaParaMostrar.map((emp, i) => (<option key={i} value={emp.terc_nom}>{buscarTodasSedes ? `${emp.empl_cargo} (${emp.area_nombre})` : emp.empl_cargo}</option>))}
              </datalist>
              <div className="mt-1 flex items-center">
                <input type="checkbox" id="buscarGlobal" checked={buscarTodasSedes} onChange={(e) => setBuscarTodasSedes(e.target.checked)} className="mr-1.5 cursor-pointer accent-fuchsia-600" disabled={!datosServer.pdv} />
                <label htmlFor="buscarGlobal" className="text-[9px] text-fuchsia-700 font-black tracking-wide cursor-pointer uppercase">Buscar en todas las sedes</label>
              </div>
            </div>
            
            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Cédula / Documento *</label>
              <input type="text" name="documento" placeholder="Automático" value={datosServer.documento || ""} onChange={handleCustomChange} className={inputClass} />
            </div>
            
            <div className="flex flex-col space-y-1.5">
              <label className="text-[10px] font-black text-slate-700 uppercase">Cargo *</label>
              <input type="text" name="cargo" placeholder="Automático" value={datosServer.cargo || ""} onChange={handleCustomChange} className={inputClass} />
            </div>
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-5 border-b border-fuchsia-200/50 pb-4 gap-3">
            <h3 className="text-xs font-black text-fuchsia-800 uppercase tracking-widest">Prácticas Higiénicas: {datosServer.genero}</h3>
            <button type="button" onClick={marcarTodoCumple} className="bg-emerald-500 hover:bg-emerald-400 text-white text-[10px] font-black px-5 py-2.5 rounded-xl shadow-sm transition-transform active:scale-95 uppercase tracking-wide cursor-pointer w-full sm:w-auto">✓ Todo Ideal (SI/NO)</button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {aspectosActuales.map(item => (
              <div key={item.id} className="flex justify-between items-center bg-white/50 p-3 rounded-xl border border-slate-100 shadow-sm">
                <span className="text-xs font-bold text-slate-700">{item.nombre}</span>
                <div className="flex gap-1.5 bg-slate-200/60 p-1 rounded-lg">
                  <button type="button" onClick={() => setRespuestaItem(item.id, 'SI')} className={`px-4 py-1.5 rounded-md text-[10px] font-black transition-all cursor-pointer ${respuestasEvaluacion[item.id] === 'SI' ? 'bg-emerald-500 text-white shadow-sm' : 'text-slate-500 hover:bg-white hover:text-slate-800'}`}>SI</button>
                  <button type="button" onClick={() => setRespuestaItem(item.id, 'NO')} className={`px-4 py-1.5 rounded-md text-[10px] font-black transition-all cursor-pointer ${respuestasEvaluacion[item.id] === 'NO' ? 'bg-red-500 text-white shadow-sm' : 'text-slate-500 hover:bg-white hover:text-slate-800'}`}>NO</button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-5 border-t border-fuchsia-200/50">
            <h3 className="text-xs font-black text-fuchsia-800 uppercase tracking-widest mb-4">Estado de Salud</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {aspectosSalud.map(item => (
                <div key={item.id} className="flex justify-between items-center bg-white/50 p-3 rounded-xl border border-slate-100 shadow-sm">
                  <span className="text-xs font-bold text-slate-700">{item.nombre}</span>
                  <div className="flex gap-1.5 bg-slate-200/60 p-1 rounded-lg">
                    <button type="button" onClick={() => setRespuestaItem(item.id, 'SI')} className={`px-4 py-1.5 rounded-md text-[10px] font-black transition-all cursor-pointer ${respuestasEvaluacion[item.id] === 'SI' ? 'bg-amber-500 text-white shadow-sm' : 'text-slate-500 hover:bg-white hover:text-slate-800'}`}>SI</button>
                    <button type="button" onClick={() => setRespuestaItem(item.id, 'NO')} className={`px-4 py-1.5 rounded-md text-[10px] font-black transition-all cursor-pointer ${respuestasEvaluacion[item.id] === 'NO' ? 'bg-red-500 text-white shadow-sm' : 'text-slate-500 hover:bg-white hover:text-slate-800'}`}>NO</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 pt-5 border-t border-slate-200/50">
            <label className="block text-[10px] font-black text-slate-700 mb-2 uppercase tracking-widest">Acción Correctiva (Si algún ítem no cumple)</label>
            <input type="text" name="accionCorrectiva" value={datosServer.accionCorrectiva || ""} onChange={handleCustomChange} placeholder="Ej: Se solicitó retirar maquillaje excesivo o reportó tos..." className="w-full h-10 bg-white/80 border border-slate-200 focus:border-fuchsia-400 rounded-xl px-4 text-xs font-medium text-slate-800 shadow-sm outline-none" />
          </div>

          <button onClick={guardarLocal} disabled={enviando} className="w-full mt-6 bg-gradient-to-r from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 text-white font-black py-4 rounded-xl shadow-lg active:scale-95 transition-all text-[11px] tracking-widest uppercase cursor-pointer border border-fuchsia-400 disabled:opacity-50">
            {enviando ? 'Guardando...' : 'Guardar Evaluación'}
          </button>
        </div>
      </div>

      {customAlert.show && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/50 backdrop-blur-3xl rounded-[32px] p-6 sm:p-8 max-w-sm w-full text-center shadow-[0_30px_90px_rgba(0,0,0,0.2)] border border-white/80">
              <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-5 shadow-sm border border-white/80 ${customAlert.type === 'success' ? 'bg-emerald-500/20 text-emerald-700' : customAlert.type === 'warning' ? 'bg-amber-500/20 text-amber-700' : 'bg-red-500/20 text-red-700'}`}>
                <span className="text-4xl">{customAlert.type === 'success' ? '✅' : customAlert.type === 'warning' ? '⚠️' : '❌'}</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mb-2.5">{customAlert.type === 'success' ? '¡Éxito!' : 'Atención'}</h3>
              <p className="text-sm text-slate-700 font-medium mb-8 leading-relaxed px-2">{customAlert.message}</p>
              <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className={`w-full py-3.5 rounded-2xl font-bold text-white transition-all shadow-lg cursor-pointer ${customAlert.type === 'success' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-500 hover:bg-amber-400'}`}>Entendido</button>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}